/**
 * ChatbotWidget Component
 * 
 * Floating chatbot interface that appears on every page.
 * Features:
 * - Minimized floating icon in bottom-right corner
 * - Expandable chat window
 * - Message history with feedback buttons
 * - Page context awareness
 * - Step-by-step walkthrough support
 * - Dismiss capability (X closes completely, minimize shows floating button)
 * - Header icon to reopen when dismissed
 * - Accessibility compliant
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useChatbot } from './ChatbotContext';
import { useAuth } from '../auth/AuthContext';
import ReactMarkdown from 'react-markdown';

// Icons
const ChatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const MinimizeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
  </svg>
);

const SendIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

const ThumbsUpIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill={filled ? "currentColor" : "none"} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
  </svg>
);

const ThumbsDownIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill={filled ? "currentColor" : "none"} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398-.306.774-1.086 1.227-1.918 1.227h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384" />
  </svg>
);

const SparklesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

// Custom link renderer for markdown
const MarkdownLink = ({ href, children }) => {
  if (href.startsWith('/')) {
    return (
      <Link to={href} className="text-indigo-600 hover:text-indigo-800 underline">
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">
      {children}
    </a>
  );
};

// Message component
const ChatMessage = ({ message, onFeedback, onAskQuestion }) => {
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackComment, setFeedbackComment] = useState('');
  const isUser = message.role === 'user';

  const handleFeedback = async (isHelpful) => {
    if (message.feedbackGiven) return;
    
    if (!isHelpful) {
      setShowFeedbackInput(true);
    } else {
      await onFeedback(message.id, true);
    }
  };

  const submitNegativeFeedback = async () => {
    await onFeedback(message.id, false, feedbackComment);
    setShowFeedbackInput(false);
    setFeedbackComment('');
  };
  
  // Process message content to extract clickable questions
  // Format: [[question text]] becomes a clickable link
  const renderContentWithClickableQuestions = (content) => {
    // Split by clickable question markers [[...]]
    const parts = content.split(/(\[\[.*?\]\])/g);
    
    // Common markdown components configuration
    const markdownComponents = {
      a: MarkdownLink,
      h1: ({ children }) => <h1 className="text-lg font-bold mt-3 mb-2">{children}</h1>,
      h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-2">{children}</h2>,
      h3: ({ children }) => <h3 className="text-sm font-bold mt-2 mb-1">{children}</h3>,
      p: ({ children }) => <p className="my-2">{children}</p>,
      ul: ({ children }) => <ul className="my-2 list-disc pl-4 space-y-1">{children}</ul>,
      ol: ({ children }) => <ol className="my-2 list-decimal pl-4 space-y-1">{children}</ol>,
      li: ({ children }) => <li className="my-0.5">{children}</li>,
      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
      code: ({ children }) => <code className="bg-gray-200 px-1 rounded text-xs">{children}</code>,
      br: () => <br />
    };
    
    if (parts.length === 1) {
      // No clickable questions, render normally
      return (
        <div className="prose prose-sm max-w-none">
          <ReactMarkdown components={markdownComponents}>
            {content}
          </ReactMarkdown>
        </div>
      );
    }
    
    // Process parts - alternate between markdown and clickable questions
    return (
      <div className="prose prose-sm max-w-none">
        {parts.map((part, index) => {
          if (part.startsWith('[[') && part.endsWith(']]')) {
            // This is a clickable question
            const question = part.slice(2, -2);
            return (
              <button
                key={index}
                onClick={() => onAskQuestion(question)}
                className="inline text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer font-medium text-left"
                title="Click to ask this question"
              >
                {question}
              </button>
            );
          } else if (part.trim()) {
            // Regular markdown content
            return (
              <ReactMarkdown
                key={index}
                components={markdownComponents}
              >
                {part}
              </ReactMarkdown>
            );
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        {/* Avatar */}
        {!isUser && (
          <div className="flex items-center gap-2 mb-1">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
              message.aiPowered 
                ? 'bg-gradient-to-br from-emerald-500 to-teal-600' 
                : 'bg-gradient-to-br from-indigo-500 to-purple-600'
            }`}>
              <SparklesIcon />
            </div>
            <span className="text-xs text-gray-500">
              CRM Assistant
              {message.aiPowered && (
                <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700">
                  AI
                </span>
              )}
            </span>
          </div>
        )}
        
        {/* Message bubble */}
        <div className={`rounded-2xl px-4 py-2.5 ${
          isUser 
            ? 'bg-indigo-600 text-white rounded-br-md' 
            : message.isError
              ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
              : 'bg-gray-100 text-gray-800 rounded-bl-md'
        }`}>
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          ) : (
            <div className="text-sm">
              {renderContentWithClickableQuestions(message.content)}
            </div>
          )}
        </div>

        {/* Feedback buttons for assistant messages */}
        {!isUser && !message.isError && message.id !== 'welcome' && (
          <div className="flex items-center gap-2 mt-1.5 ml-1">
            {!message.feedbackGiven ? (
              <>
                <button
                  onClick={() => handleFeedback(true)}
                  className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                  title="Helpful"
                >
                  <ThumbsUpIcon filled={false} />
                </button>
                <button
                  onClick={() => handleFeedback(false)}
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                  title="Not helpful"
                >
                  <ThumbsDownIcon filled={false} />
                </button>
              </>
            ) : (
              <span className={`text-xs ${message.feedback ? 'text-green-600' : 'text-red-600'}`}>
                {message.feedback ? '✓ Thanks for the feedback!' : '✓ Feedback received'}
              </span>
            )}
          </div>
        )}

        {/* Negative feedback input */}
        {showFeedbackInput && (
          <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
            <textarea
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="How can we improve? (optional)"
              className="w-full text-sm p-2 border rounded resize-none focus:outline-none focus:ring-1 focus:ring-indigo-500"
              rows={2}
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => setShowFeedbackInput(false)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={submitNegativeFeedback}
                className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
              >
                Submit
              </button>
            </div>
          </div>
        )}

        {/* Timestamp */}
        <div className={`text-xs text-gray-400 mt-1 ${isUser ? 'text-right mr-1' : 'ml-1'}`}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
};

// Quick suggestion buttons
const QuickSuggestions = ({ onSelect }) => {
  const suggestions = [
    'How do I create a customer?',
    'What is Win Rate?',
    'Show me reports',
    'Help with this page'
  ];

  return (
    <div className="flex flex-wrap gap-2 p-3 border-t bg-gray-50">
      {suggestions.map((suggestion, i) => (
        <button
          key={i}
          onClick={() => onSelect(suggestion)}
          className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full text-gray-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
        >
          {suggestion}
        </button>
      ))}
    </div>
  );
};

// Main ChatbotWidget component
const ChatbotWidget = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const {
    isOpen,
    isDismissed,
    messages,
    isLoading,
    error,
    sendMessage,
    submitFeedback,
    startNewConversation,
    toggleChatbot,
    minimizeChatbot,
    dismissChatbot,
    updateCurrentPage
  } = useChatbot();

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Update page context
  useEffect(() => {
    updateCurrentPage(location.pathname);
  }, [location.pathname, updateCurrentPage]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Don't render if not logged in
  if (!currentUser) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };

  const handleQuickSelect = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Handle clicking on a related question in the response
  const handleQuickQuestion = (question) => {
    sendMessage(question);
  };

  return (
    <>
      {/* Floating button (minimized state - hidden when dismissed) */}
      {!isOpen && !isDismissed && (
        <button
          onClick={toggleChatbot}
          className="fixed bottom-4 right-4 z-[60] w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
          aria-label="Open help chat"
        >
          <ChatIcon />
          {/* Pulse animation */}
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
          {/* Tooltip */}
          <span className="absolute right-16 bg-gray-900 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Need help? 💬
          </span>
        </button>
      )}

      {/* Chat window (expanded state) */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 z-[60] w-96 h-[550px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-slideUp">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <SparklesIcon />
              </div>
              <div>
                <h3 className="font-semibold text-sm">CRM Assistant</h3>
                <p className="text-xs text-white/80">Ask me anything!</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={startNewConversation}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="New conversation"
              >
                <RefreshIcon />
              </button>
              <button
                onClick={minimizeChatbot}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="Minimize"
              >
                <MinimizeIcon />
              </button>
              <button
                onClick={dismissChatbot}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                title="Close chatbot"
              >
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-1">
            {messages.map((message, index) => (
              <React.Fragment key={message.id}>
                <ChatMessage
                  message={message}
                  onFeedback={submitFeedback}
                  onAskQuestion={handleQuickQuestion}
                />
                {/* Feedback encouragement - show after 3 unanswered assistant messages */}
                {message.role === 'assistant' && 
                 !message.feedbackGiven && 
                 message.id !== 'welcome' && 
                 !message.isError &&
                 index === messages.length - 1 &&
                 messages.filter(m => m.role === 'assistant' && !m.feedbackGiven && m.id !== 'welcome').length >= 3 && (
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 text-center border border-indigo-100 mt-2">
                    <p className="text-xs text-indigo-700">
                      💡 <strong>Help us improve!</strong> Rate responses with 👍/👎 to make me smarter
                    </p>
                  </div>
                )}
              </React.Fragment>
            ))}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
                <span className="text-xs">Thinking...</span>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-200">
                {error}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick suggestions */}
          {messages.length <= 2 && !isLoading && (
            <QuickSuggestions onSelect={handleQuickSelect} />
          )}

          {/* Input area */}
          <form onSubmit={handleSubmit} className="p-3 border-t bg-white">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your question..."
                className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                className="w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <SendIcon />
              </button>
            </div>
            <div className="flex justify-between items-center mt-2 px-1">
              <span className="text-xs text-gray-400">
                Page: {location.pathname}
              </span>
              <Link to="/support" className="text-xs text-indigo-600 hover:underline">
                View full Support docs →
              </Link>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
